import { type Column } from "@tanstack/react-table";

export type SubRowsCellContext<T> = {
  getValue: () => unknown;
  row: { original: T };
};

export interface ColumnConfig<T> {
  id: string;
  header: string;
  accessorKey?: keyof T;
  accessorFn?: (row: T) => string | number | boolean | Date | React.ReactNode;
  cell?: (ctx: SubRowsCellContext<T>) => React.ReactNode;
  enableSorting?: boolean;
  enableColumnFilter?: boolean;
  filterType?: "text" | "select" | "date" | "number" | "multi-select";
  filterOptions?: FilterOption[];
  enableMultiSelect?: boolean;
  enableGlobalFilter?: boolean;
  enableEditing?: boolean;
  editType?: "text" | "select" | "date" | "number";
  editOptions?: FilterOption[];
  onCellEdit?: (
    value: string | number | boolean | Date,
    rowData: T
  ) => Promise<void> | void;
  size?: number;
  skeleton?: {
    width?: string;
    height?: string;
  };
  serverFilterKey?: string;
  serverSortKey?: string;

  exportConfig?: {
    exportValueFn?: (row: any) => string | number | boolean | Date | any;
    excludeFromExport?: boolean;
    exportHeader?: string;
    formatForExport?: (value: any) => string;
  };
}

export interface ServerSideConfig<T> {
  enabled: boolean;
  onDataRequest: (
    params: ServerSideParams,
    identifier?: boolean
  ) => Promise<ServerSideResponse<T>>;
  totalCount?: number;
  loading?: boolean;
  debounceMs?: number;
}

export interface ServerSideParams {
  page: number;
  limit: number;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  [key: string]: string | number | string[] | undefined;
  dateField?: string;
  dateRange?: string;
  startDate?: string;
  endDate?: string;
}

export interface ServerSideResponse<T> {
  success: boolean;
  message: string;
  data: T[];
  meta: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

export interface FilterOption {
  label: string;
  value: string | number;
}

export interface ColumnFilterProps<T> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  column: Column<T, any>;
  filterType?: string;
  filterOptions?: FilterOption[];
  theme: TableTheme;
  serverSide?: boolean;
  debounceMs?: number;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onServerFilterChange?: (columnId: string, value: any) => void;
}

export interface ViewState {
  id: string;
  name: string;
  columnVisibility: Record<string, boolean>;
  pageSize: number;
  isDefault?: boolean;
}

export interface BatchedExportConfig {
  enabled: boolean; // Enable batched export for large datasets
  threshold?: number; // Threshold above which to use batched export (number of total records)
  batchSize?: number; // Number of records to fetch per batch
  batchDelay?: number; // Delay between batch requests (ms) to reduce server load
  maxConcurrentBatches?: number; // Maximum number of concurrent batch requests
  shouldUseBatchedExport?: (totalCount: number) => boolean; // Custom function to determine if batched export should be used
}

// Default batched export configuration
export const DEFAULT_BATCHED_EXPORT_CONFIG: BatchedExportConfig = {
  enabled: true,
  threshold: 5000, // Use batched export for datasets > 1000 records
  batchSize: 2500, // Fetch 500 records per batch
  batchDelay: 200, // 100ms delay between batches
  maxConcurrentBatches: 2, // Maximum 3 concurrent requests
};

export interface EnhancedDataTableProps<T> {
  data?: T[];
  columns: ColumnConfig<T>[];
  loading?: boolean;
  skeletonRows?: number;
  emptyMessage?: string;
  onRowClick?: (item: T) => void;
  selectedRowId?: number;
  enableGlobalSearch?: boolean;
  enableColumnVisibility?: boolean;
  enablePagination?: boolean;
  enableCSVExport?: boolean;
  batchedExportConfig?: BatchedExportConfig;
  enableDateRangeFilter?: boolean;
  dateFilterField?: string;
  enableCustomDateRangeFilter?: boolean;
  globalSearchPlaceholder?: string;
  initialPageSize?: number;
  pageSizeOptions?: number[];
  enableCustomViews?: boolean;
  viewStorageKey?: string;
  onViewChange?: (view: ViewState) => void;
  hasSubRows?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getSubRows?: (item: T) => any[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  subRowsColumns?: ColumnConfig<any>[];
  expandedItems?: Set<number>;
  onToggleExpand?: (id: number) => void;
  getRowId?: (row: T) => number | string;
  theme?: "light" | "dark" | TableTheme;
  customTheme?: Partial<TableTheme>;
  serverSide?: ServerSideConfig<T>;
}

export interface ExportProgress {
  isExporting: boolean;
  stage:
    | "preparing"
    | "fetching"
    | "processing"
    | "generating"
    | "complete"
    | "error";
  totalBatches: number;
  completedBatches: number;
  currentBatch: number;
  totalRecords: number;
  processedRecords: number;
  estimatedTimeRemaining?: number;
  error?: string;
}

export interface ServerSideState {
  currentPage: number;
  pageSize: number;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  filters: Record<string, any>;
  sorting: {
    field: string | null;
    direction: "asc" | "desc" | null;
  };
  dateRange: {
    field: string | null;
    range: string;
    startDate: string | null;
    endDate: string | null;
  };
}

export interface TableTheme {
  tableShadow?: string;
  headerBg?: string;
  headerText?: string;
  rowEvenBg?: string;
  rowOdd?: string;
  rowHover?: string;
  rowText?: string;
  subRowEvenBg?: string;
  subRowOddBg?: string;
  subRowHover?: string;
  subRowText?: string;
  buttonPrimaryBg?: string;
  buttonPrimaryText?: string;
  buttonPrimaryBorder?: string;
  buttonPrimaryHover?: string;
  buttonSecondaryBg?: string;
  buttonSecondaryText?: string;
  buttonSecondaryBorder?: string;
  buttonSecondaryHover?: string;
}

// Default light theme
const lightTheme: TableTheme = {
  tableShadow: "shadow-[0_10px_20px_rgba(0,0,0,0.15)]",
  headerBg: "bg-gray-100",
  headerText: "text-gray-600",
  rowEvenBg: "bg-white",
  rowOdd: "bg-gray-50",
  rowHover: "hover:bg-blue-50",
  rowText: "text-gray-900",
  subRowEvenBg: "bg-gray-50",
  subRowOddBg: "bg-blue-50",
  subRowHover: "hover:bg-blue-50",
  subRowText: "text-gray-800",
  buttonPrimaryBg: "bg-blue-500",
  buttonPrimaryText: "text-white",
  buttonPrimaryHover: "hover:bg-blue-600",
  buttonSecondaryBg: "bg-white",
  buttonSecondaryText: "text-gray-900",
  buttonSecondaryHover: "hover:bg-gray-300",
};

// Dark theme
const darkTheme: TableTheme = {
  headerBg: "bg-gray-800",
  headerText: "text-gray-200",
  rowEvenBg: "bg-gray-900",
  rowOdd: "bg-gray-800",
  rowHover: "hover:bg-gray-700",
  rowText: "text-gray-100",
  subRowEvenBg: "bg-gray-800",
  subRowOddBg: "bg-gray-700",
  subRowHover: "hover:bg-gray-700",
  subRowText: "text-gray-200",
  buttonPrimaryBg: "bg-blue-600",
  buttonPrimaryText: "text-white",
  buttonPrimaryHover: "hover:bg-blue-700",
  buttonSecondaryBg: "bg-gray-600",
  buttonSecondaryText: "text-gray-200",
  buttonSecondaryHover: "hover:bg-gray-500",
};

// Predefined themes
export const tableThemes = {
  light: lightTheme,
  dark: darkTheme,
};
